import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { amenitiesService } from "@/services/amenitiesService";
import {
  AmenitiesFilters,
  CreateAmenityDto,
  UpdateAmenityDto,
} from "@/services/interfaces/IAmenitiesService";

export const amenityKeys = {
  all: ["amenities"] as const,
  lists: () => [...amenityKeys.all, "list"] as const,
  list: (filters?: AmenitiesFilters) =>
    [...amenityKeys.lists(), filters] as const,
  categories: (hotelId?: string) =>
    [...amenityKeys.all, "categories", hotelId] as const,
  details: () => [...amenityKeys.all, "detail"] as const,
  detail: (id: string) => [...amenityKeys.details(), id] as const,
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;

const invalidateAmenityRelatedQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: amenityKeys.all });
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
};

export const useAmenities = (filters?: AmenitiesFilters) => {
  return useQuery({
    queryKey: amenityKeys.list(filters),
    queryFn: () => amenitiesService.getAll(filters),
    enabled: !!filters?.hotelId,
  });
};

export const useAmenity = (id: string) => {
  return useQuery({
    queryKey: amenityKeys.detail(id),
    queryFn: () => amenitiesService.getById(id),
    enabled: !!id,
  });
};

export const useAmenityCategories = (hotelId?: string) => {
  return useQuery({
    queryKey: amenityKeys.categories(hotelId),
    queryFn: () => amenitiesService.getCategories(hotelId!),
    enabled: !!hotelId,
  });
};

export const useCreateAmenity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAmenityDto) => amenitiesService.create(data),
    onSuccess: () => {
      invalidateAmenityRelatedQueries(queryClient);
    },
  });
};

export const useUpdateAmenity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAmenityDto }) =>
      amenitiesService.update(id, data),
    onSuccess: (updatedAmenity) => {
      queryClient.setQueryData(
        amenityKeys.detail(updatedAmenity.id),
        updatedAmenity,
      );
      invalidateAmenityRelatedQueries(queryClient);
    },
  });
};

export const useDeleteAmenity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => amenitiesService.deleteAmenity(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: amenityKeys.detail(deletedId) });
      invalidateAmenityRelatedQueries(queryClient);
    },
  });
};
